import React, { useState } from 'react';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../components/LoadingStates';

interface LoginProps {
  // Mostra il modulo di registrazione (assente nelle schermate che non lo prevedono)
  onRegisterClick?: () => void;
}

const Login: React.FC<LoginProps> = ({ onRegisterClick }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<{email?: string; password?: string}>({});
  const { login, isLoading, authMessage } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});

    const errors: {email?: string; password?: string} = {};

    if (!email) {
      errors.email = 'Inserisci la tua email';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email non valida';
    }

    if (!password) {
      errors.password = 'Inserisci la password';
    } else if (password.length < 3) {
      errors.password = 'Password troppo corta';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Controlla i dati inseriti');
      return;
    }

    const success = await login(email, password);

    if (success) {
      toast.success('Benvenuto in ByMarke!', { duration: 3000 });
    } else {
      // Il dettaglio (credenziali errate, account in attesa di approvazione…)
      // viene mostrato nel riquadro sotto al modulo
      toast.error('Accesso non riuscito');
    }
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen min-h-[100dvh] flex flex-col items-center justify-center p-4 gap-3"
        style={{ backgroundColor: '#FAFAFB' }}
      >
        <LoadingSpinner size="lg" />
        <span className="text-sm" style={{ color: '#5B6270' }}>
          Accesso in corso...
        </span>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen min-h-[100dvh] flex items-center justify-center p-4 sm:p-6 md:p-8 relative overflow-hidden"
      style={{ backgroundColor: '#FAFAFB' }}
    >
      {/* Gradient ambient background — sottile bagliore radiale per profondità cinematica */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(19,22,28,0.03) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <motion.div
        className="w-full max-w-[340px] sm:max-w-sm md:max-w-md relative"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <motion.img
            src="/bymarke-wordmark-white.png"
            alt="ByMarke"
            className="h-7 sm:h-8 w-auto mx-auto select-none"
            style={{ filter: 'brightness(0)' }}
            draggable={false}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          />

          <h2
            className="mt-6 text-2xl sm:text-3xl font-medium"
            style={{
              color: '#13161C',
              letterSpacing: '-0.02em',
              fontFamily: "'Libre Baskerville', Georgia, serif",
              fontWeight: 400,
            }}
          >
            Bentornato
          </h2>

          <p className="mt-2 text-sm" style={{ color: '#5B6270' }}>
            Accedi alla tua Social Dashboard
          </p>
        </div>

        {/* Login Card */}
        <div
          className="p-6 sm:p-7 md:p-8 rounded-[10px]"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #EBEDF1',
            boxShadow: 'none',
          }}
        >
          {authMessage && (
            <div
              className="mb-5 rounded-md px-4 py-3 text-sm"
              style={{
                backgroundColor: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                color: '#13161C',
              }}
              role="alert"
            >
              {authMessage}
            </div>
          )}

          <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-medium uppercase tracking-[0.12em] mb-2"
                style={{ color: '#5B6270' }}
              >
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <User className={`h-4 w-4 sm:h-5 sm:w-5 ${
                    formErrors.email ? 'text-red-400' : email ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500'
                  }`} />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (formErrors.email) setFormErrors(prev => ({...prev, email: undefined}));
                  }}
                  className={`w-full pl-11 sm:pl-12 pr-3 sm:pr-4 py-3.5 text-sm sm:text-base rounded-md border touch-manipulation focus:outline-none transition-all duration-200 ${
                    formErrors.email
                      ? 'border-red-500/60 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.18)]'
                      : 'focus:border-[#13161C]'
                  }`}
                  style={{
                    backgroundColor: '#F4F5F7',
                    color: '#13161C',
                    borderColor: formErrors.email ? undefined : '#DDE0E6',
                  }}
                  placeholder="La tua email"
                />
              </div>
              {formErrors.email && (
                <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-500">{formErrors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-xs font-medium uppercase tracking-[0.12em] mb-2"
                style={{ color: '#5B6270' }}
              >
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <Lock className={`h-4 w-4 sm:h-5 sm:w-5 ${
                    formErrors.password ? 'text-red-400' : password ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500'
                  }`} />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (formErrors.password) setFormErrors(prev => ({...prev, password: undefined}));
                  }}
                  className={`w-full pl-11 sm:pl-12 pr-11 sm:pr-12 py-3.5 text-sm sm:text-base rounded-md border touch-manipulation focus:outline-none transition-all duration-200 ${
                    formErrors.password
                      ? 'border-red-500/60 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.18)]'
                      : 'focus:border-[#13161C]'
                  }`}
                  style={{
                    backgroundColor: '#F4F5F7',
                    color: '#13161C',
                    borderColor: formErrors.password ? undefined : '#DDE0E6',
                  }}
                  placeholder="La tua password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center touch-manipulation"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors active:scale-95" />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors active:scale-95" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-500">{formErrors.password}</p>
              )}
            </div>

            {/* Submit Button — giallo pieno, testo nero (FilledButton del Radar) */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 mt-2 text-sm sm:text-base font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation active:scale-[0.98] transition-all duration-200 hover:-translate-y-px"
              style={{
                backgroundColor: '#FDF751',
                color: '#13161C',
                boxShadow: 'none',
              }}
            >
              {isLoading ? 'Accesso in corso...' : 'Accedi'}
            </button>
          </form>

          {onRegisterClick && (
            <div className="mt-6 text-center">
              <p className="text-xs" style={{ color: '#5B6270' }}>
                Non hai ancora un account?{' '}
                <button
                  type="button"
                  onClick={onRegisterClick}
                  className="font-semibold underline underline-offset-2 transition-opacity hover:opacity-80"
                  style={{ color: '#13161C' }}
                >
                  Registrati
                </button>
              </p>
              <p className="mt-1.5 text-[11px]" style={{ color: '#8B92A0' }}>
                L'accesso viene attivato da un amministratore
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p
            className="text-[10px] uppercase tracking-[0.2em] font-medium"
            style={{ color: '#8B92A0' }}
          >
            ByMarke · Social Dashboard
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
